import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button } from 'components/common';
import { login, verifyRecaptcha } from 'redux/authSlice';
import { getProfileUser } from 'redux/profileUserSlice';
import ReCAPTCHA from 'react-google-recaptcha'
import PrivacyPolicy from './PrivacyPolicy';

const LoginFormOversea = ({ hasLabel }) => {
  const recaptcha = useRef();
  const [recapchaMessage, setRecapchaMessage] = useState("");

  // State
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const from = location.state?.from || "/";

  // Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get the reCAPTCHA value
    const captchaValue = recaptcha.current.getValue();

    if (!captchaValue) {
      setRecapchaMessage('Please verify the reCAPTCHA!');
      return;
    }

    try {
      // Verify reCAPTCHA with backend
      const recaptchaResponse = await dispatch(verifyRecaptcha(captchaValue));

      if (!recaptchaResponse.payload.success) {
        setRecapchaMessage('Token is invalid or expired!');
        toast.error('reCAPTCHA verification failed. Please try again.');
        return;
      }

      setRecapchaMessage('');

      // Attempt login
      const loginResponse = await dispatch(
        login({
          user: {
            email: formData.email,
            password: formData.password,
          },
        })
      );

      // Fetch profile after successful login
      if (loginResponse.meta.requestStatus === 'fulfilled') {
        await dispatch(getProfileUser());
        navigate(from, { replace: true });
      } else {
        const errMsg = loginResponse.payload || 'Login failed. Please check your credentials.';
        toast.error(errMsg);
      }
    } catch (error) {
      // General error handling
      if (error.response && error.response.data) {
        toast.error(error.response.data.message || 'An error occurred while signing in.');
      } else {
        toast.error('An error occurred while signing in.');
      }
    }
  };

  const handleFieldChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRecapchaChange = () => {
    setRecapchaMessage("");
  }

  const hasValue = !formData.email || !formData.password;

  return (
    <form onSubmit={handleSubmit}>
      <div className="tw-mb-3">
        {hasLabel && <label className="tw-text-sm tw-font-medium tw-text-slate-700">Email address</label>}
        <input
          className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-solid tw-border-slate-300 tw-rounded-md tw-bg-white tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
          placeholder={!hasLabel ? 'Email address' : ''}
          value={formData.email}
          name="email"
          onChange={handleFieldChange}
          type="email"
        />
      </div>

      <div className="tw-mb-3">
        {hasLabel && <label className="tw-text-sm tw-font-medium tw-text-slate-700">Password</label>}
        <input
          className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-solid tw-border-slate-300 tw-rounded-md tw-bg-white tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
          placeholder={!hasLabel ? 'Password' : ''}
          value={formData.password}
          name="password"
          onChange={handleFieldChange}
          type="password"
        />
      </div>

      <div className="tw-flex tw-justify-between tw-items-center">
        <div>
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-0">
            <input
              className="tw-w-4 tw-h-4"
              type="checkbox"
              id="rememberMe"
              name="remember"
              checked={formData.remember}
              onChange={e =>
                setFormData({
                  ...formData,
                  remember: e.target.checked
                })
              }
            />
            <label className="tw-text-sm tw-mb-0 tw-text-slate-600" htmlFor="rememberMe">
              Remember me
            </label>
          </div>
        </div>

        <div>
          <Link
            className="tw-text-sm tw-mb-0"
            to={`/authentication/forgot-password`}
          >
            Forgot Password?
          </Link>
        </div>
      </div>

      <div>
        {!hasValue && (
          <div className="tw-p-2">
            <ReCAPTCHA
              ref={recaptcha}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={handleRecapchaChange}
            />
            <div className="tw-text-sm tw-text-red-600">{recapchaMessage}</div>
          </div>
        )}

        <Button
          type="submit"
          className="tw-mt-3 tw-w-full"
          disabled={hasValue}
        >
          Log in
        </Button>
      </div>

      <PrivacyPolicy />
    </form>
  );
};

LoginFormOversea.propTypes = {
  layout: PropTypes.string,
  hasLabel: PropTypes.bool
};

LoginFormOversea.defaultProps = {
  layout: 'simple',
  hasLabel: false
};

export default LoginFormOversea;
